/**
 * TanStack Query hooks for Recurring Invoices
 * Pattern: Component → Hook → Service → API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recurringInvoicesService } from '../services/billing.service';
import type {
  RecurringInvoiceStatus,
  CreateRecurringInvoiceDTO,
  UpdateRecurringInvoiceDTO,
} from '../types/billing.types';

// ============================================================
// Query Keys
// ============================================================
export const recurringInvoiceKeys = {
  all: ['recurring-invoices'] as const,
  lists: () => [...recurringInvoiceKeys.all, 'list'] as const,
  list: (filters?: { page?: number; limit?: number; status?: RecurringInvoiceStatus }) =>
    [...recurringInvoiceKeys.lists(), filters] as const,
  details: () => [...recurringInvoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...recurringInvoiceKeys.details(), id] as const,
  stats: () => [...recurringInvoiceKeys.all, 'stats'] as const,
};

// ============================================================
// Query Hooks
// ============================================================

/**
 * Hook to list recurring invoices with optional pagination/filters
 */
export function useRecurringInvoices(params?: {
  page?: number;
  limit?: number;
  status?: RecurringInvoiceStatus;
}) {
  return useQuery({
    queryKey: recurringInvoiceKeys.list(params),
    queryFn: () => recurringInvoicesService.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to get a single recurring invoice by ID
 */
export function useRecurringInvoice(id: string | undefined) {
  return useQuery({
    queryKey: recurringInvoiceKeys.detail(id!),
    queryFn: () => recurringInvoicesService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to get recurring invoice stats
 */
export function useRecurringInvoiceStats() {
  return useQuery({
    queryKey: recurringInvoiceKeys.stats(),
    queryFn: () => recurringInvoicesService.getStats(),
    staleTime: 2 * 60 * 1000,
  });
}

// ============================================================
// Mutation Hooks
// ============================================================

/**
 * Hook to create a recurring invoice
 */
export function useCreateRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRecurringInvoiceDTO) =>
      recurringInvoicesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}

/**
 * Hook to update a recurring invoice
 */
export function useUpdateRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRecurringInvoiceDTO }) =>
      recurringInvoicesService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.stats() });
    },
  });
}

/**
 * Hook to pause a recurring invoice
 */
export function usePauseRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringInvoicesService.pause(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}

/**
 * Hook to resume a recurring invoice
 */
export function useResumeRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringInvoicesService.resume(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}

/**
 * Hook to cancel a recurring invoice
 */
export function useCancelRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringInvoicesService.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}

/**
 * Hook to generate an invoice manually
 */
export function useGenerateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringInvoicesService.generateInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}

/**
 * Hook to delete a recurring invoice
 */
export function useDeleteRecurringInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => recurringInvoicesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
    },
  });
}
